import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Switch,
  FormControlLabel,
  Typography,
  Alert,
  CircularProgress,
  Box,
  TextField
} from '@mui/material';
import axios from 'axios';
import { connectorService } from '../../services/connectorService';
import CronBuilder from './CronBuilder';

export default function CreateScheduleModal({ open, onClose, onSuccess, onError, preSelectedConnectorId }) {
  const [formData, setFormData] = useState({
    connector_id: '',
    schedule_type: 'daily',
    cron_expression: '0 2 * * *',
    sync_window_days: 3,
    intraday_window_days: 0,
    is_enabled: true
  });

  const [connectors, setConnectors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingConnectors, setLoadingConnectors] = useState(false);
  const [errors, setErrors] = useState({});
  const [isCustomWindow, setIsCustomWindow] = useState(false);

  const WINDOW_PRESETS = [3, 7, 14, 30];
  const WINDOW_MIN = 1;
  const WINDOW_MAX = 90;
  const INTRADAY_MIN = 0;

  // Load available connectors without existing schedules
  useEffect(() => {
    if (open) {
      loadConnectors();
    }
  }, [open]);

  // Pre-select connector if provided
  useEffect(() => {
    if (preSelectedConnectorId && open && connectors.length > 0) {
      console.log('Pre-selecting connector:', preSelectedConnectorId);
      console.log('Available connectors:', connectors.map(c => ({ id: c.id, name: c.name })));
      
      // Convert to string for comparison since IDs might be numbers
      const connectorId = String(preSelectedConnectorId);
      const matchingConnector = connectors.find(c => String(c.id) === connectorId);
      
      if (matchingConnector) {
        console.log('Found matching connector:', matchingConnector.name);
        setFormData(prev => ({ ...prev, connector_id: matchingConnector.id }));
      } else {
        console.warn('No matching connector found for ID:', preSelectedConnectorId);
      }
    }
  }, [preSelectedConnectorId, open, connectors]);

  const loadConnectors = async () => {
    try {
      setLoadingConnectors(true);
      
      // Get all connectors and existing schedules
      const [connectorsResponse, schedulesResponse] = await Promise.all([
        connectorService.getConnections(),
        axios.get('/api/connectors/schedules')
      ]);
      
      // Extract connectors from the service response
      const allConnectors = connectorsResponse.connectors || connectorsResponse || [];
      const existingSchedules = schedulesResponse.data.schedules || [];
      
      // Create a set of connector IDs that already have schedules
      const scheduledConnectorIds = new Set(
        existingSchedules.map(s => String(s.connector_id))
      );
      
      // Add hasSchedule flag to all connectors
      const connectorsWithScheduleInfo = allConnectors.map(connector => ({
        ...connector,
        hasSchedule: scheduledConnectorIds.has(String(connector.id))
      }));
      
      setConnectors(connectorsWithScheduleInfo);
    } catch (error) {
      console.error('Error loading connectors:', error);
      onError('Failed to load connectors');
    } finally {
      setLoadingConnectors(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.connector_id) {
      newErrors.connector_id = 'Connector is required';
    }

    const windowNum = Number(formData.sync_window_days);
    if (!Number.isInteger(windowNum) || windowNum < WINDOW_MIN || windowNum > WINDOW_MAX) {
      newErrors.sync_window_days = `Data window must be a whole number between ${WINDOW_MIN} and ${WINDOW_MAX} days`;
    }

    const intradayNum = Number(formData.intraday_window_days);
    if (!Number.isInteger(intradayNum) || intradayNum < INTRADAY_MIN || intradayNum > WINDOW_MAX) {
      newErrors.intraday_window_days = `Intraday window must be a whole number between ${INTRADAY_MIN} and ${WINDOW_MAX} days`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);
      await axios.post('/api/connectors/schedules', {
        ...formData,
        intraday_window_days: Number(formData.intraday_window_days) || 0
      });
      onSuccess();
    } catch (error) {
      console.error('Error creating schedule:', error);
      onError(error.response?.data?.message || 'Failed to create schedule');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      connector_id: '',
      schedule_type: 'daily',
      cron_expression: '0 2 * * *',
      sync_window_days: 3,
      intraday_window_days: 0,
      is_enabled: true
    });
    setIsCustomWindow(false);
    setErrors({});
    onClose();
  };

  const handleWindowSelectChange = (e) => {
    const val = e.target.value;
    if (val === 'custom') {
      setIsCustomWindow(true);
      // Keep the current numeric value so the TextField has a starting point
      if (!WINDOW_PRESETS.includes(Number(formData.sync_window_days))) {
        // already custom-like; leave as-is
      } else {
        handleInputChange('sync_window_days', formData.sync_window_days);
      }
    } else {
      setIsCustomWindow(false);
      handleInputChange('sync_window_days', val);
    }
  };

  const handleCustomWindowChange = (e) => {
    const raw = e.target.value;
    if (raw === '') {
      handleInputChange('sync_window_days', '');
      return;
    }
    const parsed = parseInt(raw, 10);
    if (Number.isNaN(parsed)) return;
    handleInputChange('sync_window_days', parsed);
  };

  const handleIntradayWindowChange = (e) => {
    const raw = e.target.value;
    if (raw === '') {
      handleInputChange('intraday_window_days', '');
      return;
    }
    const parsed = parseInt(raw, 10);
    if (Number.isNaN(parsed)) return;
    handleInputChange('intraday_window_days', parsed);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Create Scheduled Job</DialogTitle>
      
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={3}>
            {/* Connector Selection */}
            <Grid item xs={12}>
              <FormControl fullWidth error={!!errors.connector_id}>
                <InputLabel>Connector</InputLabel>
                <Select
                  value={formData.connector_id}
                  label="Connector"
                  onChange={(e) => handleInputChange('connector_id', e.target.value)}
                  disabled={loadingConnectors}
                >
                  {connectors.map(connector => (
                    <MenuItem 
                      key={connector.id} 
                      value={connector.id}
                      disabled={connector.hasSchedule}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="body1">{connector.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {connector.platform_name} • {connector.brand_name || 'No Brand'}
                          </Typography>
                        </Box>
                        {connector.hasSchedule && (
                          <Typography variant="caption" sx={{ color: 'success.main', ml: 2 }}>
                            ✅ Scheduled
                          </Typography>
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {errors.connector_id && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {errors.connector_id}
                  </Alert>
                )}
              </FormControl>
              
              {connectors.length === 0 && !loadingConnectors && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  All your connectors already have schedules configured.
                </Alert>
              )}
            </Grid>

            {/* Schedule Configuration */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                When should this run?
              </Typography>
              <CronBuilder
                value={formData.cron_expression}
                onChange={(value) => handleInputChange('cron_expression', value)}
                error={errors.cron_expression}
              />
            </Grid>

            {/* Sync Window Days */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Data Window</InputLabel>
                <Select
                  value={isCustomWindow ? 'custom' : formData.sync_window_days}
                  label="Data Window"
                  onChange={handleWindowSelectChange}
                >
                  <MenuItem value={3}>3 Days (Recommended)</MenuItem>
                  <MenuItem value={7}>1 Week</MenuItem>
                  <MenuItem value={14}>2 Weeks</MenuItem>
                  <MenuItem value={30}>1 Month</MenuItem>
                  <MenuItem value="custom">Custom...</MenuItem>
                </Select>
              </FormControl>
              {isCustomWindow && (
                <TextField
                  fullWidth
                  type="number"
                  label="Custom window (days)"
                  value={formData.sync_window_days}
                  onChange={handleCustomWindowChange}
                  inputProps={{ min: WINDOW_MIN, max: WINDOW_MAX, step: 1 }}
                  error={!!errors.sync_window_days}
                  helperText={errors.sync_window_days || `Enter a whole number between ${WINDOW_MIN} and ${WINDOW_MAX}`}
                  sx={{ mt: 1 }}
                />
              )}
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                How many days of historical data to sync each time
              </Typography>
              {!isCustomWindow && errors.sync_window_days && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {errors.sync_window_days}
                </Alert>
              )}
            </Grid>

            {/* Intraday Window Days (later runs of the same UTC day) */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Intraday window (later runs)"
                value={formData.intraday_window_days}
                onChange={handleIntradayWindowChange}
                inputProps={{ min: INTRADAY_MIN, max: WINDOW_MAX, step: 1 }}
                error={!!errors.intraday_window_days}
                helperText={errors.intraday_window_days || '0 = same as data window. For multi-run-per-day schedules: the FIRST run of the UTC day uses the data window; later runs use this shorter window (days).'}
              />
            </Grid>

            {/* Enable Schedule */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_enabled}
                    onChange={(e) => handleInputChange('is_enabled', e.target.checked)}
                  />
                }
                label="Enable this schedule immediately"
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                The scheduled job will run according to the cron expression specified above
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button 
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || loadingConnectors || connectors.length === 0}
        >
          {loading && <CircularProgress size={20} sx={{ mr: 1 }} />}
          Create Schedule
        </Button>
      </DialogActions>
    </Dialog>
  );
}
